import { Injectable, inject } from '@angular/core';
import {
    SignInOutput,
    SignUpOutput,
    resetPassword,
    confirmResetPassword,
    signIn,
    signUp
} from 'aws-amplify/auth';
import { PasswordService } from '../services/password/password.service';
import { environment } from '@env/environment';
import { ROLES } from '@app/constants';
import { ClientSettingService } from '../api/services/client-setting.service';

export interface LoginContext {
    username: string;
    password: string;
    remember: boolean;
}

export interface RegisterContext {
    name?: string;
    email?: string;
    role?: string;
    firstName?: string;
    lastName?: string;
    phoneNumber?: string;
    companyId?: string;
    brandId?: string;
    /**
     * Si es true, no infiere `companyId` por dominio del correo (alta manual en admin, etc.).
     * Se usa solo `companyId` del contexto; si falta, aplica el fallback de `signUp`.
     */
    skipEmailDomainCompanyId?: boolean;
}

@Injectable({
    providedIn: 'root'
})
export class AuthenticationService {
    private _passwordService = inject(PasswordService);
    private _clientSettingService = inject(ClientSettingService);

    constructor() {}

    changePassword(email: string): Promise<any> {
        return resetPassword({ username: email });
    }

    confirmChangePassword(
        username: string,
        confirmationCode: string,
        newPassword: string
    ): Promise<any> {
        return confirmResetPassword({
            username,
            confirmationCode,
            newPassword
        });
    }

    login(username: string, password: string): Promise<SignInOutput> {
        return signIn({ username, password });
    }

    async register(
        context: RegisterContext,
        role: string | null
    ): Promise<SignUpOutput> {
        let password: string = null;

        while (password === null) {
            password = this._passwordService.generatePass();
        }

        const isSpecialProvider =
            context.role === ROLES.PROVEEDOR_SD ||
            context.role === ROLES.LOGISTIC;

        let finalCompanyId = context.companyId;

        if (
            !context.skipEmailDomainCompanyId &&
            !finalCompanyId &&
            context.email
        ) {
            finalCompanyId = await this.getClientIdByEmail(context.email);
        }

        const userRole = context.role || role || ROLES.COMPRAS_SR;
        const userName = context.name
            ? context.name.trim()
            : `${context.firstName?.trim()} ${context.lastName?.trim()}`;

        return signUp({
            username: context.email.trim().toLowerCase(),
            password,
            options: {
                userAttributes: {
                    email: context.email.trim().toLowerCase(),
                    name: userName,
                    'custom:role': userRole,
                    'custom:companyId':
                        finalCompanyId || environment.sourceDataClientId,
                    'custom:brandId':
                        context.brandId && context.brandId.length <= 2
                            ? context.brandId
                            : '',
                    'custom:phoneNumber': context.phoneNumber || ''
                }
            }
        });
    }

    async getClientIdByEmail(email: string): Promise<string> {
        try {
            if (!email || !email.includes('@')) {
                return environment.sourceDataClientId;
            }

            const domain = email
                .split('@')[1]
                ?.toLowerCase()
                .trim();

            if (!domain) {
                return environment.sourceDataClientId;
            }

            const clientSetting = await this._clientSettingService.getClientSettingByDomain(
                domain
            );

            const finalClientId =
                (clientSetting as any)?.clientId ||
                clientSetting?.id ||
                environment.sourceDataClientId;

            return finalClientId;
        } catch (error) {
            console.error(
                'Error obteniendo clientId por dominio, usando default:',
                error
            );
            return environment.sourceDataClientId;
        }
    }
}
